"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MapPin, MessageSquare, Calendar, ArrowRight, CircleCheck as CheckCircle2, Sparkles, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "faq-1",
  question: "How quickly can my Derby business expect results from local SEO?",
  answer: "While full organic authority takes 3 to 6 months to mature, local Google Business Profile improvements and targeted local map pack optimizations in Derby often deliver noticeable lead increases within 4 to 8 weeks. I prioritize quick-win local search terms to generate real enquiries from Derbyshire customers early on.",
  tag: "Timeline & ROI"
}, {
  id: "faq-2",
  question: "Why should I work directly with you instead of hiring a Derby SEO agency?",
  answer: "When you hire an agency, your account is frequently handed off to junior staff or account managers. With me, you work directly with Yassine Essaadi. I handle every aspect of your local SEO strategy, audit, and implementation personally—ensuring deep local insight, agility, and absolute accountability for your Derby rankings.",
  tag: "Direct Consultant"
}, {
  id: "faq-3",
  question: "How do you optimize my Google Business Profile for local Derby searches?",
  answer: "I conduct a thorough audit of your Google Business Profile, audit primary and secondary categories, correct NAP (Name, Address, Phone) consistency across local UK directories, create geotagged updates, write optimized services copy, and establish a review generation system tailored to your Derby audience.",
  tag: "GBP Optimization"
}, {
  id: "faq-4",
  question: "Can local SEO help my business reach customers in Belper, Ripley, Ashbourne, and Pride Park?",
  answer: "Yes, absolutely. Local SEO goes beyond the city centre. I build targeted local service area pages and geo-specific content strategies designed to capture high-converting search intent across surrounding Derbyshire areas, including Pride Park, Belper, Ripley, Duffield, and Ashbourne.",
  tag: "Derbyshire Reach"
}, {
  id: "faq-5",
  question: "What is the difference between local SEO and national SEO strategy?",
  answer: "Local SEO focuses on capturing geographically intent-driven searches—like 'SEO consultant in Derby' or 'plumber near me'—primarily through Google Map Packs and localized search results. National SEO targets broad keywords across the UK without geographic bounds. For local service providers in Derby, local SEO provides the highest immediate conversion rates.",
  tag: "SEO Strategy"
}, {
  id: "faq-6",
  question: "Do I need physical premises in Derby city centre to rank for local searches?",
  answer: "Not necessarily. If you operate as a Service Area Business (SAB)—such as a tradesperson, mobile consultant, or local contractor—we can configure your Google Business Profile and local landing pages to highlight your service area across Derby without publicly displaying a home address.",
  tag: "Business Type"
}, {
  id: "faq-7",
  question: "How do you handle local citations and backlink building in Derbyshire?",
  answer: "I focus on quality over volume. I secure genuine, high-authority UK business listings, local Derbyshire directories, regional press mentions, and relevant industry citations. No automated spam or toxic link networks—only clean, authoritative links that build long-term local trust.",
  tag: "Local Links"
}, {
  id: "faq-8",
  question: "Will your local SEO strategy help drive real phone calls and foot traffic?",
  answer: "Yes. Rankings are simply a means to an end. My local strategy focuses on conversion rate optimization for local audiences—optimizing call-to-action buttons, direct click-to-call links, Google Maps directions, and trust signals on your site so visitors quickly convert into paying clients.",
  tag: "Conversions"
}, {
  id: "faq-9",
  question: "How do you integrate AI search optimization with local SEO for Derby queries?",
  answer: "Modern searchers increasingly use AI platforms like Google Gemini, ChatGPT, and AI Overviews for local recommendations. I structure your business information with schema markup, structured local data, and authoritative brand mentions so AI models reliably recommend your Derby services.",
  tag: "AI Search"
}, {
  id: "faq-10",
  question: "Are there long-term contracts when I hire you as my SEO consultant?",
  answer: "No restrictive multi-year lock-ins. I work on flexible monthly consultancy terms because I believe my performance and real business results should be the reason you choose to keep working with me month after month.",
  tag: "Terms & Pricing"
}, {
  id: "faq-11",
  question: "How do we track success and local rankings in Derby?",
  answer: "You receive transparent, jargon-free monthly reports directly from me. We track local map pack positions across specific Derby postcode sectors, organic rankings, phone call conversions, contact form submissions, and overall website traffic growth.",
  tag: "Reporting"
}, {
  id: "faq-12",
  question: "How do I get started with a local SEO consultation for my Derby business?",
  answer: "You can book a free 30-minute consultation directly through my site. We will discuss your current local visibility in Derby, your growth goals, and I will share practical insights into how we can outperform your local competitors.",
  tag: "Getting Started"
}];
export default function Localseofaq() {
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const shouldReduceMotion = useReducedMotion();
  const categories = ["All", "GBP Optimization", "Timeline & ROI", "Derbyshire Reach", "Direct Consultant"];
  const filteredFaqs = selectedTag === "All" ? faqList : faqList.filter(faq => faq.tag === selectedTag || selectedTag === "All");
  return <section id="localseofaq" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 size-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5 bg-secondary text-secondary-foreground border-border">
              <MapPin className="size-3.5 text-primary" />
              Derby Local SEO Expertise
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Clear Answers for Your <span className="text-primary">Derby Local SEO</span> Questions
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            No fluff, no agency sales talk. Here is exactly how I help businesses across Derby and Derbyshire dominate local search, rank higher on Google Maps, and win sustainable local clients.
          </motion.p>

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          ease: "easeOut"
        }} className="mt-8 flex flex-wrap justify-center gap-2">
            {categories.map((category, idx) => <Button key={category} data-index={idx} variant={selectedTag === category ? "default" : "outline"} size="sm" onClick={() => setSelectedTag(category)} className={`rounded-full text-xs font-medium transition-[color,background-color,border-color] ${selectedTag === category ? "bg-primary text-primary-foreground shadow-xs" : "border-border text-foreground hover:bg-muted"}`}>
                {category}
              </Button>)}
          </motion.div>
        </div>

        {}
        <div className="max-w-4xl mx-auto">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="bg-card rounded-2xl border border-border p-4 sm:p-8 shadow-md">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {filteredFaqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border/80 rounded-xl px-4 sm:px-6 py-1 bg-background transition-[border-color,box-shadow] hover:border-primary/40 focus-within:border-primary/50">
                  <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-primary hover:no-underline py-4 py-3 sm:py-4 gap-3">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="size-5 shrink-0 text-primary opacity-80 hidden sm:inline-block" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-0 sm:pl-8">
                    <p className="mb-3">{faq.answer}</p>
                    <div className="flex items-center gap-2 pt-2 text-xs font-medium text-foreground/80">
                      <Badge variant="outline" className="text-xs font-normal border-primary/20 text-primary bg-primary/5">
                        {faq.tag}
                      </Badge>
                      <span className="text-muted-foreground">• Direct insight from Yassine</span>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-primary/10 via-card to-accent/10 border border-border rounded-2xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-3 text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="size-4" />
                <span>Have a Specific Derby SEO Question?</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                You work directly with me. No account managers.
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
                Let us discuss your Derby business, analyze your current local search visibility, and build a tailored strategy to get you to the top of Google Maps.
              </p>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 pt-2 text-xs text-foreground/80">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Free 30-Min Consultation
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Tailored Local Audit
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Transparent Pricing
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-xl font-medium shadow-md group">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                  <span>Book Free Consultation</span>
                  <ArrowRight className="size-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-xl border-border bg-background hover:bg-muted text-foreground">
                <a href="mailto:contact@ye-consultancy.com">
                  <MessageSquare className="size-4 mr-2 text-primary" />
                  <span>Email Yassine Directly</span>
                </a>
              </Button>
            </div>
          </div>
        </motion.div>

        {}
        <div className="mt-12 text-center text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-center gap-2">
          <span className="flex items-center gap-1">
            <Phone className="size-3.5 text-primary" />
            <a href="tel:+447490984430" className="hover:underline font-medium text-foreground">+44 7490984430</a>
          </span>
          <span className="hidden sm:inline">•</span>
          <span>YE Consultancy — Independent SEO Specialist for UK, National & Derby Businesses</span>
        </div>

      </div>
    </section>;
}